import { Injectable } from '@angular/core';
import { combineLatest, Observable, of } from 'rxjs';
import { catchError, distinctUntilChanged, map, shareReplay } from 'rxjs/operators';
import { OutboundCallerIdCanaryConfig } from 'src/app/models/outbound-caller-id-canary.model';
import { Patient } from 'src/app/models/patient.model';
import { NewAuthService } from './auth/new-auth-service.service';
import { FirestoreService } from './firestore.service';

/**
 * Decides whether this user has stopped choosing the outbound caller ID by hand (NP-04).
 *
 * Reads {@link OutboundCallerIdCanaryConfig} from `welby_configuration/featureToggles` in real time and
 * cross-references the authenticated user's email, so the pilot group can be changed — or the rollout
 * killed — from Firestore without a deploy. Mirrors {@link GapsViewerCanaryService}.
 *
 * Fails closed, and here that means **keeping the manual dropdown**: a missing, disabled or malformed
 * config leaves every user with the behaviour that shipped before this canary. That is the safe
 * direction for this particular toggle — the failure mode of the opposite default would be a nurse
 * with no dropdown and no derivable line, unable to place a call at all.
 *
 * The user side is derived from `isLoggedObservable$` (emits on every login/logout) so a logout resets
 * the evaluation and a shared workstation never inherits the previous user's group.
 */
@Injectable({
  providedIn: 'root',
})
export class OutboundCallerIdCanaryService {
  private static readonly CONFIG_PATH = 'welby_configuration/featureToggles';
  private static readonly FEATURE_KEY = 'outboundCallerIdCanary';

  readonly isEnabled$: Observable<boolean> = combineLatest([
    this.firestore.doc$(OutboundCallerIdCanaryService.CONFIG_PATH).pipe(catchError(() => of(null))),
    this.auth.isLoggedObservable$.pipe(map((isLoggedIn) => (isLoggedIn ? this.auth.user : null))),
  ]).pipe(
    map(([config, user]) => this.evaluate(config?.[OutboundCallerIdCanaryService.FEATURE_KEY], user)),
    distinctUntilChanged(),
    shareReplay({ bufferSize: 1, refCount: false })
  );

  // Declared after `isEnabled$` to satisfy member-ordering (public before private). Field initialisers
  // run after the constructor's parameter properties are assigned, so the order is free here.
  private enabledCache = false;

  constructor(private firestore: FirestoreService, private auth: NewAuthService) {
    this.isEnabled$.subscribe((enabled) => (this.enabledCache = enabled));
  }

  /** Synchronous snapshot. Fails closed until the config resolves, i.e. the manual dropdown stays. */
  get isEnabled(): boolean {
    return this.enabledCache;
  }

  /**
   * True when this user's caller ID is chosen automatically. Requires `enabled` plus an authenticated
   * user; then only `allowAll: true` (explicit opt-in) reaches everyone. An empty/omitted allowlist —
   * or one made up solely of blank entries — resolves to nobody, so flipping `enabled` before the group
   * is populated takes the dropdown away from no one. Otherwise the email must be listed.
   */
  private evaluate(config: OutboundCallerIdCanaryConfig | undefined, user: Patient | undefined): boolean {
    try {
      if (!config?.enabled || !user) {
        return false;
      }
      if (config.allowAll) {
        return true;
      }
      // Non-string and blank entries are dropped up front so a hand-edited Firestore doc (a number in
      // `emails`, a stray '') can neither throw nor match, and the length reflects real recipients.
      const emails = (config.emails ?? [])
        .filter((allowed) => typeof allowed === 'string')
        .map((allowed) => allowed.trim().toLowerCase())
        .filter((allowed) => !!allowed);
      if (emails.length === 0 || !user.email) {
        return false;
      }
      return emails.includes(user.email.trim().toLowerCase());
    } catch {
      // Any unexpected/malformed config can do no more than keep the manual dropdown, never error the
      // `isEnabled$` stream (which would freeze `enabledCache` at its last value for this singleton).
      return false;
    }
  }
}
